import { EASE_OUT } from './easing';
import React, { useRef, useState } from 'react';
import { motion, useMotionValueEvent, useScroll, useTransform } from 'framer-motion';

interface HeroSectionProps {
  started: boolean;
  title: string;
}

/**
 * The landing frame. It holds no video of its own: `#hero-reel-slot` simply
 * reserves the rectangle that ReelPlayer morphs out of, so the reel can dock
 * into a corner player without ever being remounted.
 *
 * Note there is no transform on this section. A transformed ancestor would
 * become the containing block for the fixed player and pin it in place.
 */
const HeroSection: React.FC<HeroSectionProps> = ({ started, title }) => {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  // Do not move these back onto style={{ opacity: motionValue }}.
  //
  // framer-motion 13.1.1 does not update style.opacity from a MotionValue
  // here, while transform properties on the very same element do. Isolated
  // with a probe on a preview channel: at scrollYProgress 0.75 the derived
  // value read 0 exactly as it should, yet the element still carried
  // opacity: 1. So the value is right and the binding is what fails, which is
  // why markY animated while copyOpacity, slotFill and markOpacity sat frozen
  // and the hero's whole leave-behind sequence never ran. 13.1.1 is the latest
  // release, so there is nothing to upgrade to.
  //
  // Reading progress into state and animating a plain number is the way round
  // it. tests/site.spec.ts guards this so it cannot silently die again.
  const [progress, setProgress] = useState(0);
  useMotionValueEvent(scrollYProgress, 'change', setProgress);

  const span = (v: number, a: number, b: number) => Math.min(1, Math.max(0, (v - a) / (b - a)));
  const copyOpacity = 1 - span(progress, 0, 0.22);
  // as the reel docks away, the card stops being a black hole: the dark fill
  // dissolves to reveal the live gradient behind, and the name resolves in
  const slotFill = 1 - span(progress, 0, 0.45);
  const markOpacity = span(progress, 0.3, 0.75);
  const markY = useTransform(scrollYProgress, [0.3, 0.75], [40, 0]);

  const reveal = {
    hidden: { y: '110%' },
    visible: (i: number) => ({
      y: 0,
      transition: { duration: 0.9, delay: 0.3 + i * 0.08, ease: EASE_OUT },
    }),
  };

  return (
    <section
      ref={ref}
      id="top"
      // Desktop fills the viewport, because the reel card fills the hero.
      // A phone cannot: the reel is 16:9 and the screen is portrait, so the
      // card plus its copy came to ~470px inside a reserved 844px, leaving
      // ~180px dead above and ~230px below. Sizing to the content with a
      // minimum keeps the opening substantial while letting the first work
      // card show at the bottom edge, which is a cue to scroll rather than a
      // hole.
      // The top padding on a phone is clearance, not decoration: the header is
      // fixed, and without it the card slid under the logo and the Menu pill,
      // which then sat on the reel. Desktop needs none — there the card fills
      // the viewport and the chrome is meant to float over it.
      className="relative min-h-[64svh] md:h-[100svh] px-2.5 pb-2.5 pt-[88px] md:p-4 flex flex-col justify-center md:block"
    >
      {/* The design has no large headline, but the document still needs a
          single H1 for search engines and screen readers. The headings
          otherwise start at H2 with nothing above them. */}
      <h1 className="sr-only">
        Hamlet Hayrapetyan, Motion Graphics Designer, Animator and 3D Generalist in Los Angeles
      </h1>

      {/* the rectangle the player occupies while undocked */}
      {/* Square on a phone. The ratio is a dial between height and how much of
          a 16:9 frame survives: on a 370px-wide phone 4:3 is 278px tall and
          keeps ~75% of the width, 1:1 is 370px tall and keeps ~56%. Losing the
          sides is the accepted trade for a reel that reads as the page's
          subject rather than as a strip near the top. */}
      <div className="relative w-full aspect-square md:contents">
      <div
        id="hero-reel-slot"
        className="absolute inset-0 rounded-[24px] overflow-hidden border border-ink/10
                   md:inset-4 md:rounded-[36px]"
      >
        <motion.div animate={{ opacity: slotFill }} transition={{ duration: 0 }} className="absolute inset-0 bg-card" />

        {/* what the reel leaves behind */}
        <motion.div
          animate={{ opacity: markOpacity }}
          transition={{ duration: 0 }}
          style={{ y: markY }}
          className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center pointer-events-none"
        >
          <span className="font-grotesk font-semibold text-[9vw] md:text-[6vw] leading-none tracking-tight text-transparent text-outline-ink">
            Hamlet Hayrapetyan
          </span>
          <span className="font-grotesk text-[10px] md:text-xs uppercase tracking-[0.3em] text-ink/65">
            Motion · 3D · XR
          </span>
        </motion.div>
      </div>

      </div>

      {/* Under the card the background is paper, so these read in ink. Kept
          mid-screen rather than pinned to the bottom, where they collided with
          the fixed sound button. */}
      <motion.div animate={{ opacity: copyOpacity }} transition={{ duration: 0 }} className="md:hidden mt-5 px-1.5">
        <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-ink">{title}</p>
        <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-ink/65 mt-1.5">
          Showreel 2026 · Los Angeles
        </p>
      </motion.div>

      {/* copy sits above the fixed player */}
      <motion.div animate={{ opacity: copyOpacity }} transition={{ duration: 0 }} className="hidden md:block absolute inset-0 z-[75] pointer-events-none">
        <div className="absolute left-6 md:left-10 bottom-6 md:bottom-9 max-w-[60%] md:max-w-none">
          <div className="overflow-hidden">
            <motion.p
              variants={reveal}
              custom={0}
              initial="hidden"
              animate={started ? 'visible' : 'hidden'}
              className="font-grotesk text-[10px] md:text-xs uppercase tracking-[0.3em] text-white drop-shadow-[0_1px_6px_rgba(0,0,0,0.8)]"
            >
              {title}
            </motion.p>
          </div>
          <div className="overflow-hidden mt-1">
            <motion.p
              variants={reveal}
              custom={1}
              initial="hidden"
              animate={started ? 'visible' : 'hidden'}
              className="font-grotesk text-[10px] md:text-xs uppercase tracking-[0.3em] text-white drop-shadow-[0_1px_6px_rgba(0,0,0,0.8)]"
            >
              Showreel 2026
            </motion.p>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
